import { useAction, useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  MEDIA_CONSTRAINTS,
  validateSopFiles,
} from "@domain/media-constraints";
import {
  composeMediaUploadBlock,
  composeMediaUploadBlocks,
  type MediaUploadRef,
} from "@domain/media-upload-block";
import {
  getBeadWriter,
  threadLabel,
  type BeadValueObject,
} from "@domain/praxis-beads";
import { buildFilePreviewUrl } from "@/lib/file-url";
import {
  createWsManager,
  type WsEvent,
  type WsManager,
  type WsState,
} from "@/lib/wsManager";
import {
  useChatBridgeStore,
  type ResolverChoiceArgs,
} from "@/store/chatBridgeStore";
import { useChatPanelStore } from "@/store/chatPanelStore";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";

export interface ToolEvent {
  id: string;
  name: string;
  args: string;
  result?: string;
}

const DEV_BASE_WS_URL = "ws://localhost:42617/ws/chat";
const BASE_WS_URL = "wss://gw.clawcraft.ca/ws/chat";

/**
 * Messages fetched per history page. Sized to fill a tall viewport on open
 * without reading a whole long thread — the rest arrives via "load earlier".
 */
const MESSAGE_PAGE_SIZE = 50;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function normalizeUploadMimeType(t: string): string {
  return t || "application/octet-stream";
}

// Private helper shared by sendMessage and sendSopFiles. Generates a signed
// upload URL, PUTs the file, and returns the GCS path + the pod-workspace
// path the agent reads from. Pure transport logic — no React, no state.
async function uploadFileToGcs(args: {
  file: File;
  threadId: Id<"threads">;
  generateUploadUrl: (a: {
    flow: "conversation-attachments";
    filename: string;
    mimeType: string;
    threadId: Id<"threads">;
  }) => Promise<{ uploadUrl: string; gcsPath: string }>;
}): Promise<{ gcsPath: string; workspacePath: string }> {
  const { file, threadId, generateUploadUrl } = args;
  const mimeType = normalizeUploadMimeType(file.type);
  const { uploadUrl, gcsPath } = await generateUploadUrl({
    flow: "conversation-attachments" as const,
    filename: file.name,
    mimeType,
    threadId,
  });
  // An unchecked failed PUT would still persist the MEDIA_UPLOAD block below —
  // the agent would be told a file exists that never landed in GCS.
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": mimeType },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Upload failed for ${file.name} (HTTP ${res.status})`);
  }
  const relativePath = gcsPath.split("/").slice(1).join("/");
  const workspacePath = `/zeroclaw-data/workspace/${relativePath}`;
  return { gcsPath, workspacePath };
}

// Shared body of the file-ingestion capabilities (sop-ingestion +
// reference-ingestion): parallel-uploads the files, persists ONE synthetic
// user bubble carrying the joined [MEDIA_UPLOAD] blocks + attachments, and
// returns the refs for the caller's directive. The two capabilities differ
// only in the WS directive they send afterwards (frontend-doctrine §4.10:
// one upload path).
async function uploadFilesAndPersistSynthetic(args: {
  files: File[];
  threadId: Id<"threads">;
  generateUploadUrl: Parameters<typeof uploadFileToGcs>[0]["generateUploadUrl"];
  persistSyntheticUserMessage: (a: {
    threadId: Id<"threads">;
    clientMessageId: string;
    mediaUploadBlocks: string;
    attachments: {
      gcsPath: string;
      filename: string;
      mimeType: string;
      size: number;
    }[];
  }) => Promise<unknown>;
}): Promise<{ refs: MediaUploadRef[] }> {
  const { files, threadId, generateUploadUrl, persistSyntheticUserMessage } =
    args;
  const clientMessageId = crypto.randomUUID();

  const uploads = await Promise.all(
    files.map(async (file) => {
      const { gcsPath, workspacePath } = await uploadFileToGcs({
        file,
        threadId,
        generateUploadUrl,
      });
      const mimeType = file.type || "application/octet-stream";
      return {
        ref: {
          type: mimeType,
          name: file.name,
          path: workspacePath,
          size: file.size,
        } satisfies MediaUploadRef,
        attachment: {
          gcsPath,
          filename: file.name,
          mimeType,
          size: file.size,
        },
      };
    }),
  );

  const refs = uploads.map((u) => u.ref);
  const attachments = uploads.map((u) => u.attachment);

  await persistSyntheticUserMessage({
    threadId,
    clientMessageId,
    mediaUploadBlocks: composeMediaUploadBlocks(refs),
    attachments,
  });

  return { refs };
}

// ─── [CLAWCRAFT_DIRECTIVE] composers (client-WS-issued, claw-doctrine §5.6.2) ─

/**
 * Pure composer for `Source: bead-forward` and `Source: validation-forward`
 * directives. Exported so unit tests can pin the wire shape without
 * standing up the whole useChat hook (Convex queries / WS / audio).
 *
 * Branches on `getBeadWriter(bead) === "validation" && bead.role === "prior_work"`.
 * The validation branch carries a `ResolverContext:` line + asks the
 * agent for direct-fix OR an inline `[RESOLVER_OPTIONS]` render tag.
 */
export function composeBeadDirective(bead: BeadValueObject): string {
  // biome-ignore lint/suspicious/noExplicitAny: convex row carries _id + shortId
  const b = bead as any;
  const description =
    "description" in bead.fields && bead.fields.description
      ? bead.fields.description
      : "(none)";
  const beadIdentifier = (b.shortId ?? b._id ?? "unknown") as string;
  const isValidationPriorWork =
    getBeadWriter(bead) === "validation" && bead.role === "prior_work";

  if (isValidationPriorWork) {
    return [
      "[CLAWCRAFT_DIRECTIVE]",
      "Source: validation-forward",
      `ResolverContext: ${beadIdentifier}`,
      "",
      "The user clicked Ask AI on a validation finding. Read the finding,",
      "then choose ONE of the following response shapes:",
      "",
      "1. DIRECT FIX. If the resolution is unambiguous, respond in chat",
      "   with the concrete fix or next action. Do not emit a tag.",
      "",
      "2. A/B/C OPTIONS. If two reasonable approaches exist, emit a",
      "   [RESOLVER_OPTIONS] tag (inline in your assistant message) with",
      "   this exact JSON shape:",
      "",
      `     [RESOLVER_OPTIONS]{"context":"${beadIdentifier}","optionA":{"label":"<one-line A>"},"optionB":{"label":"<one-line B>"},"optionC":{"placeholder":"Describe the override..."}}[/RESOLVER_OPTIONS]`,
      "",
      "   The `context` field MUST echo the ResolverContext line above",
      "   verbatim. Option C is always a user-override slot — when the",
      "   user picks C, you will receive a follow-up directive carrying",
      "   their typed text; obey it verbatim.",
      "",
      "Precede the tag (if any) with a one-sentence framing of the",
      "choice; do not summarize after.",
      "",
      "--- VALIDATION FINDING ---",
      `ID: ${beadIdentifier}`,
      `Title: ${bead.fields.title}`,
      `Type: ${bead.type}`,
      `State: ${bead.state}`,
      "Description:",
      description,
      "--- END FINDING ---",
      "[/CLAWCRAFT_DIRECTIVE]",
    ].join("\n");
  }

  return [
    "[CLAWCRAFT_DIRECTIVE]",
    "Source: bead-forward",
    "",
    "The user has forwarded this task from their task manager. Read the bead,",
    "take any reasonable next step (or ask a clarifying question), and respond",
    "in chat with what you did or propose to do.",
    "",
    "--- BEAD START ---",
    `ID: ${beadIdentifier}`,
    `Title: ${bead.fields.title}`,
    `Type: ${bead.type}`,
    `State: ${bead.state}`,
    `Assignee: ${bead.metadata?.assignee ?? "unassigned"}`,
    "Description:",
    description,
    "--- BEAD END ---",
    "[/CLAWCRAFT_DIRECTIVE]",
  ].join("\n");
}

/**
 * Pure composer for `Source: resolver-choice` directives — sent when the
 * user clicks A/B/C in the in-bubble ResolverChoice widget.
 *
 * `Context:` echoes the token from the agent's [RESOLVER_OPTIONS] tag so
 * the agent can correlate the choice with the original finding.
 * `UserText:` is only emitted when `Choice: C` (free-text override).
 */
export function composeResolverChoiceDirective(args: ResolverChoiceArgs): string {
  const lines = [
    "[CLAWCRAFT_DIRECTIVE]",
    "Source: resolver-choice",
    `Context: ${args.context}`,
    `Choice: ${args.choice}`,
  ];
  if (args.choice === "C" && args.userText) {
    lines.push("UserText:", args.userText);
  }
  lines.push(
    "",
    args.choice === "C"
      ? "The user chose option C and provided override text. Obey UserText verbatim."
      : `The user chose option ${args.choice}. Proceed with that option's approach.`,
    "[/CLAWCRAFT_DIRECTIVE]",
  );
  return lines.join("\n");
}

/**
 * Pure composer for `Source: sop-ingestion` directives — emitted when the
 * user drops 1..N files into the SpecTab "Upload an SOP" dropzone. The
 * directive instructs the agent to materialize one `reference` bead per
 * file (workspace-durable ground truth), then disambiguate the workflow
 * into a praxis spec.
 *
 * The persisted synthetic user bubble carries only the joined
 * `[MEDIA_UPLOAD]` blocks — this full prose body is WS-only (live
 * ingestion instructions are ephemeral, claw-doctrine §5.6.2).
 */
export function composeSopIngestionDirective(args: {
  refs: readonly MediaUploadRef[];
  threadId: string;
}): string {
  const { refs, threadId } = args;
  const blocks = composeMediaUploadBlocks(refs);
  return [
    "[CLAWCRAFT_DIRECTIVE]",
    "Source: sop-ingestion",
    "",
    `The user dropped ${refs.length} document(s) via the "Upload an SOP" dropzone.`,
    "",
    "For EACH [MEDIA_UPLOAD] block below:",
    "  1. Read the file at the path.",
    "  2. Extract its text content. If the extracted text exceeds 256 KB,",
    "     truncate at 256 KB and append the marker:",
    "       [truncated — N more bytes in source file at <path>]",
    "  3. Write the (possibly-truncated) text to a temp file.",
    `  4. Run: praxis create "<filename>" -t reference --file <tmp-path>`,
    "     (This creates one `reference` bead per file — ground truth for",
    "     the source document. Reference beads are workspace-durable.)",
    "",
    "Then, using the collected reference beads as ground truth:",
    "  5. Disambiguate the workflow described across the documents into a",
    "     complete praxis spec. If anything is genuinely ambiguous, ask the",
    "     user ONE focused clarifying question in chat before proceeding.",
    "     Do NOT invent fields the documents don't support.",
    "  6. Write a prose body to a temp file and a factory body to a second",
    "     temp file, then run:",
    `       praxis spec create --thread ${threadId} \\`,
    "         --from-file <prose-tmp> --factory-from-file <factory-tmp>",
    "",
    blocks,
    "[/CLAWCRAFT_DIRECTIVE]",
  ].join("\n");
}

/**
 * Pure composer for `Source: reference-ingestion` directives — emitted when
 * the user drops 1..N files into the workflow Reference tab's dropzone.
 * Ingestion ONLY: one thread-labeled `reference` bead per file, and —
 * unlike sop-ingestion — explicitly NO spec authoring and no execution.
 *
 * The thread label rides IN the command (sop-ingestion beads instead rely
 * on the title===filename match), so the Reference tab's label filter
 * picks the beads up directly.
 */
export function composeReferenceIngestionDirective(args: {
  refs: readonly MediaUploadRef[];
  threadId: string;
}): string {
  const { refs, threadId } = args;
  const blocks = composeMediaUploadBlocks(refs);
  return [
    "[CLAWCRAFT_DIRECTIVE]",
    "Source: reference-ingestion",
    "",
    `The user dropped ${refs.length} document(s) into this workflow's Reference tab.`,
    "",
    "For EACH [MEDIA_UPLOAD] block below:",
    "  1. Read the file at the path.",
    "  2. Extract its text content. If the extracted text exceeds 256 KB,",
    "     truncate at 256 KB and append the marker:",
    "       [truncated — N more bytes in source file at <path>]",
    "  3. Write the (possibly-truncated) text to a temp file.",
    `  4. Run: praxis create "<filename>" -t reference --file <tmp-path> -l ${threadLabel(threadId)}`,
    "     (One workspace-durable `reference` bead per file, tagged to this",
    "     thread so the Reference tab can scope it. Trust the",
    '     "✓ Created <id> [reference]" line as the receipt — do NOT re-verify.)',
    "",
    "This is reference ingestion ONLY:",
    "  - Do NOT create, update, or modify any spec. `praxis spec create` and",
    "    `praxis spec update` are OUT OF SCOPE for this directive.",
    "  - Do NOT start an execution.",
    "",
    "When all files are ingested, reply with ONE short line in chat naming",
    'the saved file(s) (e.g. "Saved 2 reference files: pricing.pdf, faq.txt").',
    "",
    blocks,
    "[/CLAWCRAFT_DIRECTIVE]",
  ].join("\n");
}

export function useChat({
  threadId,
  authToken,
}: {
  threadId: Id<"threads">;
  authToken: string | null;
}) {
  // Cursor-paginated, newest page first. There is deliberately NO ceiling on
  // thread length: page 1 stays reactive so a new message always lands in it,
  // and older history is pulled on demand via `loadMore`. (The prior
  // `listByThread` read took the OLDEST 200 rows, which silently made every
  // message past 200 unreadable — writes succeeded, the UI just never saw them.)
  const {
    results: descMessages,
    status: pageStatus,
    loadMore,
  } = usePaginatedQuery(
    api.messages.listByThreadPage,
    { threadId },
    { initialNumItems: MESSAGE_PAGE_SIZE },
  );
  const persistUser = useMutation(api.messages.persistUserMessage);
  const persistAssistant = useMutation(api.messages.persistAssistantMessage);
  const persistSyntheticUserMessage = useMutation(
    api.messages.persistSyntheticUserMessage,
  );
  const generateUploadUrl = useAction(api.gcsActions.generateUploadUrl);

  // Dev-only: pull preSharedToken from the authenticated user's own row to
  // wire the local-loop WS auth automatically. Convex deduplicates query
  // subscriptions, so this piggybacks on AuthGuard's existing `getMe` call —
  // no extra network. Prod path doesn't read this (uses Convex Auth JWT).
  // See infra-doctrine §15.24.
  const me = useQuery(api.users.getMeSafe);

  const [streamingContent, setStreamingContent] = useState("");
  // Ephemeral inner-thought stream (ZeroClaw `thinking` frames). Cleared on
  // `done` and at the start of each new turn — not persisted to history.
  const [thinkingContent, setThinkingContent] = useState("");
  const [toolEvents, setToolEvents] = useState<ToolEvent[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [wsState, setWsState] = useState<WsState>("disconnected");

  const wsRef = useRef<WsManager | null>(null);
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;

  // Track the current turn's assistant clientMessageId
  const assistantMsgIdRef = useRef("");

  // Ref mirrors of the accumulators so the WS `done` handler can read the
  // final thinking/tools to persist, without putting them in the effect deps
  // (which would reconnect the socket on every streamed delta).
  const thinkingRef = useRef("");
  const toolEventsRef = useRef<ToolEvent[]>([]);

  // Pre-loaded audio for mobile — must be unlocked during a user gesture (sendMessage)
  const doneAudioRef = useRef<HTMLAudioElement | null>(null);
  if (typeof window !== "undefined" && !doneAudioRef.current) {
    doneAudioRef.current = new Audio("/assets/audio/task-completed.wav");
  }

  useEffect(() => {
    if (!authToken) return;

    let url = "";
    const isDev = window.location.hostname === "localhost";
    if (isDev) {
      // sessionStorage fallback retained for emergency override (devtools paste);
      // primary path is the auto-pulled preSharedToken from the user's own row.
      const devToken =
        me?.preSharedToken ?? sessionStorage.getItem("zeroclaw_token");
      if (!devToken) return;  // wait for `me` to land or for manual override
      url = `${DEV_BASE_WS_URL}?token=${encodeURIComponent(devToken)}`;
    } else {
      url = `${BASE_WS_URL}?token=${encodeURIComponent(authToken)}`;
    }

    const onEvent = (event: WsEvent) => {
      switch (event.type) {
        case "chunk":
          setIsWaitingForResponse(false);
          setIsStreaming(true);
          setStreamingContent((prev) => prev + event.content);
          break;

        case "thinking":
          // Inner thoughts arrive before the answer. Drop the waiting
          // indicator and accumulate the reasoning deltas.
          setIsWaitingForResponse(false);
          setIsStreaming(true);
          setThinkingContent((prev) => {
            const next = prev + event.content;
            thinkingRef.current = next;
            return next;
          });
          break;

        case "tool_call":
          setIsWaitingForResponse(false);
          setIsStreaming(true);
          setToolEvents((prev) => {
            // ZeroClaw may send args as a structured object; the persisted
            // schema (and ToolEvent) require a string. Normalize at ingestion.
            const rawArgs: unknown = event.args;
            const next = [
              ...prev,
              {
                name: event.name,
                args:
                  typeof rawArgs === "string"
                    ? rawArgs
                    : JSON.stringify(rawArgs),
                result: undefined,
                id: crypto.randomUUID(),
              },
            ];
            toolEventsRef.current = next;
            return next;
          });
          break;

        case "tool_result":
          // FMEA E2: Match by first pending (no result) position, not by name
          setToolEvents((prev) => {
            const idx = prev.findIndex((t) => t.result === undefined);
            if (idx === -1) return prev;
            const rawOut: unknown = event.output;
            const output =
              typeof rawOut === "string" ? rawOut : JSON.stringify(rawOut);
            const next = prev.map((t, i) =>
              i === idx ? { ...t, result: output } : t,
            );
            toolEventsRef.current = next;
            return next;
          });
          break;

        case "done": {
          if (doneAudioRef.current && !useChatPanelStore.getState().soundsMuted) {
            doneAudioRef.current.currentTime = 0;
            doneAudioRef.current.play().catch(() => {});
          }
          const fullResponse = event.full_response;
          const clientMessageId =
            assistantMsgIdRef.current || crypto.randomUUID();
          const toolCalls = toolEventsRef.current.length
            ? toolEventsRef.current.map(({ name, args, result }) => ({
                name,
                args,
                result,
              }))
            : undefined;
          persistAssistant({
            content: fullResponse,
            threadId: threadIdRef.current,
            clientMessageId,
            thinking: thinkingRef.current || undefined,
            toolCalls,
          }).catch(() =>
            // Never lose the answer to a widget-payload rejection — retry
            // with text only (same clientMessageId keeps it idempotent).
            persistAssistant({
              content: fullResponse,
              threadId: threadIdRef.current,
              clientMessageId,
            }).catch(() => {}),
          );
          setIsWaitingForResponse(false);
          setIsStreaming(false);
          setStreamingContent("");
          setThinkingContent("");
          setToolEvents([]);
          thinkingRef.current = "";
          toolEventsRef.current = [];
          break;
        }

        case "error":
          console.error("[ws] agent error:", event.message);
          setIsWaitingForResponse(false);
          setIsStreaming(false);
          break;

        case "history":
          break;
      }
    };

    const ws = createWsManager({
      url,
      onEvent,
      onStateChange: setWsState,
    });
    wsRef.current = ws;

    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [authToken, me?.preSharedToken, persistAssistant]);

  // `descMessages` accumulates newest-first (page 1 = newest, later pages older),
  // so a single reverse yields render order (oldest → newest). `toReversed` is
  // avoided for baseline reasons; slice()+reverse() keeps the source immutable.
  const messages = useMemo(
    () =>
      descMessages
        .slice()
        .reverse()
        .map((msg) => ({
          ...msg,
          attachments: msg.attachments?.map((att) => ({
            ...att,
            url: att.gcsPath ? buildFilePreviewUrl(att.gcsPath) : undefined,
          })),
        })),
    [descMessages],
  );

  const sendMessage = useCallback(
    async ({ content, files }: { content: string; files?: File[] }) => {
      const clientMessageId = crypto.randomUUID();
      assistantMsgIdRef.current = crypto.randomUUID();

      let attachments:
        | Array<{
            gcsPath: string;
            filename: string;
            mimeType: string;
            size: number;
          }>
        | undefined;
      let base64Images: Array<{ data: string; mimeType: string }> | undefined;

      let workspacePaths: Map<string, string> | undefined;
      if (files?.length) {
        // Upload to GCS via signed URLs; base64 only for images (skip for PDF/audio)
        const results = await Promise.all(
          files.map(async (file) => {
            const isImage = file.type.startsWith("image/");
            const [upload, base64] = await Promise.all([
              uploadFileToGcs({ file, threadId, generateUploadUrl }),
              isImage ? fileToBase64(file) : Promise.resolve(null),
            ]);
            return {
              gcsPath: upload.gcsPath,
              workspacePath: upload.workspacePath,
              base64,
              file,
            };
          }),
        );

        attachments = results.map((r) => ({
          gcsPath: r.gcsPath,
          filename: r.file.name,
          mimeType: r.file.type || "application/octet-stream",
          size: r.file.size,
        }));

        workspacePaths = new Map(
          results.map((r) => [r.gcsPath, r.workspacePath]),
        );

        // Only include small images as inline base64 for snappy vision
        base64Images = results
          .filter(
            (r) =>
              r.base64 &&
              r.file.type.startsWith("image/") &&
              r.file.size <= MEDIA_CONSTRAINTS.inlineBase64ThresholdBytes,
          )
          .map((r) => ({ data: r.base64!, mimeType: r.file.type }));
      }

      // Build [MEDIA_UPLOAD] context blocks for the agent
      const mediaBlocks = attachments
        ?.map((att) => {
          const workspacePath =
            workspacePaths?.get(att.gcsPath) ??
            `/zeroclaw-data/workspace/${att.gcsPath.split("/").slice(1).join("/")}`;
          return composeMediaUploadBlock({
            type: att.mimeType,
            name: att.filename,
            path: workspacePath,
            size: att.size,
          });
        })
        .join("\n");

      const contentWithMedia =
        mediaBlocks ? `${mediaBlocks}\n\n${content}` : content;

      // Persist user message (fire and forget)
      persistUser({
        content,
        threadId,
        clientMessageId,
        attachments,
      }).catch(() => {});

      // Unlock audio on mobile — play+pause during user gesture so it can replay later
      if (doneAudioRef.current) {
        const a = doneAudioRef.current;
        a.muted = true;
        a.play()
          .then(() => {
            a.pause();
            a.muted = false;
            a.currentTime = 0;
          })
          .catch(() => {});
      }

      // Reset streaming state for new turn
      setIsWaitingForResponse(true);
      setStreamingContent("");
      setThinkingContent("");
      setToolEvents([]);
      thinkingRef.current = "";
      toolEventsRef.current = [];

      // Send over WS. The typed `threadId` field tells ZeroClaw which thread
      // this message belongs to (used for spec filenames, media organization,
      // and the projection's `<threadId>.spec.md` convention).
      wsRef.current?.send({
        type: "message",
        threadId,
        content: contentWithMedia,
        ...(base64Images?.length ? { images: base64Images } : {}),
      });
    },
    [persistUser, threadId, generateUploadUrl],
  );

  // Forward a bead from the task manager into the agent loop. "Behind the
  // scenes" — does NOT persist to message history (the agent's response
  // materializes in chat without a preceding user bubble). Mirrors the
  // auto-prompt precedent above (lines 192-196). Wrapped in [CLAWCRAFT_DIRECTIVE]
  // (claw-doctrine §5.6.1 / §5.6.2) so the agent recognizes it as authoritative
  // instructions, not user-channel conversation.
  //
  // Validation prior-work beads take a specialized "validation-forward"
  // body that asks for direct-fix OR an A/B/C [RESOLVER_OPTIONS] tag the
  // chat surface parses into the in-bubble ResolverChoice widget.
  const sendBeadToAgent = useCallback((bead: BeadValueObject) => {
    if (!wsRef.current) return;
    assistantMsgIdRef.current = crypto.randomUUID();
    setIsWaitingForResponse(true);
    setStreamingContent("");
    setThinkingContent("");
    setToolEvents([]);
    thinkingRef.current = "";
    toolEventsRef.current = [];
    wsRef.current.send({
      type: "message",
      threadId: threadIdRef.current,
      content: composeBeadDirective(bead),
    });
  }, []);

  // SOP ingestion drop: 1..N PDF/text files dropped on SpecTab's empty
  // state. Uploads in parallel, persists ONE synthetic user bubble carrying
  // the joined [MEDIA_UPLOAD] blocks, then sends a sop-ingestion directive
  // over WS. See claw-doctrine §5.6.2.
  const sendSopFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      if (validateSopFiles(files) !== null) return;

      assistantMsgIdRef.current = crypto.randomUUID();
      setIsWaitingForResponse(true);
      setStreamingContent("");
      setToolEvents([]);

      const { refs } = await uploadFilesAndPersistSynthetic({
        files,
        threadId,
        generateUploadUrl,
        persistSyntheticUserMessage,
      });

      wsRef.current?.send({
        type: "message",
        threadId,
        content: composeSopIngestionDirective({ refs, threadId }),
      });
    },
    [threadId, generateUploadUrl, persistSyntheticUserMessage],
  );

  // Reference-ingestion drop: 1..N PDF/text files dropped on the workflow
  // Reference tab's dropzone. Same upload+persist body as sendSopFiles;
  // the directive creates thread-labeled reference beads ONLY (no spec
  // authoring). See claw-doctrine §5.6.2.
  const sendReferenceFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      if (validateSopFiles(files) !== null) return;

      assistantMsgIdRef.current = crypto.randomUUID();
      setIsWaitingForResponse(true);
      setStreamingContent("");
      setToolEvents([]);

      const { refs } = await uploadFilesAndPersistSynthetic({
        files,
        threadId,
        generateUploadUrl,
        persistSyntheticUserMessage,
      });

      wsRef.current?.send({
        type: "message",
        threadId,
        content: composeReferenceIngestionDirective({ refs, threadId }),
      });
    },
    [threadId, generateUploadUrl, persistSyntheticUserMessage],
  );

  // Send the user's response to an A/B/C resolver widget back into the
  // agent loop as a follow-up [CLAWCRAFT_DIRECTIVE]. Same transport as
  // sendBeadToAgent — not persisted to history. The `Context:` line
  // echoes the token the agent emitted in its [RESOLVER_OPTIONS] tag so
  // the agent can correlate the choice with the original finding.
  const sendResolverChoice = useCallback((args: ResolverChoiceArgs) => {
    if (!wsRef.current) return;
    assistantMsgIdRef.current = crypto.randomUUID();
    setIsWaitingForResponse(true);
    setStreamingContent("");
    setThinkingContent("");
    setToolEvents([]);
    thinkingRef.current = "";
    toolEventsRef.current = [];
    wsRef.current.send({
      type: "message",
      threadId: threadIdRef.current,
      content: composeResolverChoiceDirective(args),
    });
  }, []);

  // Publish sendBeadToAgent to the chat bridge so sibling panels (TasksTab)
  // can invoke it without prop-drilling or lifting useChat to ThreadView.
  // Registered for the lifetime of useChat (regardless of wsState) so the
  // forward button is enabled on the chat route even during transient WS
  // reconnects; the underlying wsManager handles queuing for send-while-
  // disconnected. Cleared on unmount so panels on routes WITHOUT useChat
  // (dashboard, etc.) correctly see the button as disabled.
  const setBridgeSend = useChatBridgeStore((s) => s.setSend);
  const clearBridgeSend = useChatBridgeStore((s) => s.clearSend);
  const setBridgeResolverSend = useChatBridgeStore((s) => s.setResolverSend);
  const clearBridgeResolverSend = useChatBridgeStore((s) => s.clearResolverSend);
  const setBridgeSopSend = useChatBridgeStore((s) => s.setSopSend);
  const clearBridgeSopSend = useChatBridgeStore((s) => s.clearSopSend);
  const setBridgeReferenceSend = useChatBridgeStore((s) => s.setReferenceSend);
  const clearBridgeReferenceSend = useChatBridgeStore(
    (s) => s.clearReferenceSend,
  );
  useEffect(() => {
    setBridgeSend(sendBeadToAgent);
    setBridgeResolverSend(sendResolverChoice);
    setBridgeSopSend(sendSopFiles);
    setBridgeReferenceSend(sendReferenceFiles);
    return () => {
      clearBridgeSend();
      clearBridgeResolverSend();
      clearBridgeSopSend();
      clearBridgeReferenceSend();
    };
  }, [
    sendBeadToAgent,
    sendResolverChoice,
    sendSopFiles,
    sendReferenceFiles,
    setBridgeSend,
    clearBridgeSend,
    setBridgeResolverSend,
    clearBridgeResolverSend,
    setBridgeSopSend,
    clearBridgeSopSend,
    setBridgeReferenceSend,
    clearBridgeReferenceSend,
  ]);

  // Single-turn serialization signal — true while the /ws/chat agent loop
  // is producing output for the current turn (awaiting first chunk OR
  // mid-stream). ChatInput (parent gates via ChatWindow) and ResolverChoice
  // (reads from bridge store) consume this to prevent firing a second turn
  // on top of the first. See frontend-doctrine §4.3.
  const isAgentActive = isStreaming || isWaitingForResponse;

  const setBridgeIsAgentActive = useChatBridgeStore((s) => s.setIsAgentActive);
  useEffect(() => {
    setBridgeIsAgentActive(isAgentActive);
    return () => setBridgeIsAgentActive(false);
  }, [isAgentActive, setBridgeIsAgentActive]);

  return {
    /** True while an older history page is in flight (§4.3 load-earlier). */
    canLoadMore: pageStatus === "CanLoadMore",
    isAgentActive,
    isLoading: pageStatus === "LoadingFirstPage",
    isLoadingMore: pageStatus === "LoadingMore",
    isStreaming,
    isWaitingForResponse,
    /** Pull the next older page of history. */
    loadMore: () => loadMore(MESSAGE_PAGE_SIZE),
    messages,
    sendBeadToAgent,
    sendMessage,
    sendReferenceFiles,
    sendResolverChoice,
    sendSopFiles,
    streamingContent,
    thinkingContent,
    toolEvents,
    wsState,
  };
}
